import React, { useState } from "react";
import { useDeleteQuote, useQuotes, useTransitionQuote, openQuotePdf } from "../../api/quotes";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { EditIconButton, DeleteIconButton, PdfIconButton } from "../../components/RowIconButton";
import { formatBRL, formatDate } from "../../utils/money";
import { NewQuoteModal } from "./NewQuoteModal";
import { useToast } from "../../components/ToastProvider";
import { Quote, QuoteStatus } from "../../types/dtos";

const FILTERS: Array<{ label: string; status?: QuoteStatus }> = [
  { label: "Todos" },
  { label: "Rascunho", status: "RASCUNHO" },
  { label: "Enviado", status: "ENVIADO" },
  { label: "Aprovado", status: "APROVADO" },
  { label: "Rejeitado", status: "REJEITADO" },
];

function quoteTotal(quote: Quote): number {
  return quote.items.reduce((acc, item) => acc + Number(item.value), 0);
}

export default function QuotesPage() {
  const [filter, setFilter] = useState<QuoteStatus | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Quote | null>(null);
  const { data, isLoading, isError } = useQuotes({ page, pageSize: 7, status: filter });
  const transition = useTransitionQuote();
  const deleteQuote = useDeleteQuote();
  const toast = useToast();

  async function handleTransition(id: number, status: QuoteStatus, label: string) {
    try {
      await transition.mutateAsync({ id, status });
      toast(`Orçamento ${label}.`);
    } catch {
      toast("Não foi possível atualizar o orçamento.");
    }
  }

  async function handleDelete(id: number, code: string) {
    if (!window.confirm(`Excluir o orçamento ${code}? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteQuote.mutateAsync(id);
      toast(`${code} excluído.`);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(message ?? "Não foi possível excluir.");
    }
  }

  async function handlePdf(id: number) {
    try {
      await openQuotePdf(id);
    } catch {
      toast("Não foi possível gerar o PDF.");
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-3">
          <label className="nt-field-label">Situação</label>
          <select
            className="form-select"
            value={filter ?? ""}
            onChange={(e) => {
              setFilter((e.target.value || undefined) as QuoteStatus | undefined);
              setPage(1);
            }}
          >
            {FILTERS.map((f) => (
              <option key={f.label} value={f.status ?? ""}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6" />
        <div className="col-md-3">
          <label className="nt-field-label d-none d-md-block">&nbsp;</label>
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Novo orçamento
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar os orçamentos.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Orçamento</th>
                <th>Cliente / lead</th>
                <th style={{ width: 120 }}>Data</th>
                <th className="text-end" style={{ width: 120 }}>
                  Total
                </th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 260 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((q) => (
                <tr key={q.id}>
                  <td className="nt-tabular">{q.code}</td>
                  <td style={{ fontFamily: "var(--font-heading)" }}>
                    {q.customer ? q.customer.razaoSocial : q.leadName}
                    {!q.customer && (
                      <span className="text-secondary" style={{ fontSize: 11, fontFamily: "var(--font-body)" }}>
                        {" "}
                        · lead
                      </span>
                    )}
                  </td>
                  <td className="nt-tabular">{formatDate(q.createdAt)}</td>
                  <td className="text-end nt-tabular">{formatBRL(quoteTotal(q))}</td>
                  <td>
                    <StatusTag status={q.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {q.status === "RASCUNHO" && <EditIconButton onClick={() => setEditing(q)} />}
                      <PdfIconButton onClick={() => handlePdf(q.id)} />
                      {q.status === "RASCUNHO" && (
                        <button type="button" className="btn btn-ghost" style={{ fontSize: 12.5 }} onClick={() => handleTransition(q.id, "ENVIADO", "enviado")}>
                          Enviar
                        </button>
                      )}
                      {q.status === "ENVIADO" && (
                        <>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: 12.5, color: "var(--sp-success)" }}
                            onClick={() => handleTransition(q.id, "APROVADO", "aprovado")}
                          >
                            Aprovar
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost text-danger"
                            style={{ fontSize: 12.5 }}
                            onClick={() => handleTransition(q.id, "REJEITADO", "rejeitado")}
                          >
                            Rejeitar
                          </button>
                        </>
                      )}
                      {q.status === "RASCUNHO" && <DeleteIconButton onClick={() => handleDelete(q.id, q.code)} disabled={deleteQuote.isPending} />}
                    </div>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-secondary text-center py-4">
                    Nenhum orçamento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewQuoteModal show={showNew} editing={null} onClose={() => setShowNew(false)} />
      <NewQuoteModal show={!!editing} editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
